import { useCallback, useEffect, useState } from 'react';
import {
  ArrowUturnLeftIcon,
  ExclamationTriangleIcon,
  PhotoIcon,
  TrashIcon,
} from '@heroicons/react/24/outline';
import type { Locale, TrashAsset, TrashPurgeRequestInput } from '@xphoto/shared';
import { TRASH_DEFAULT_LIMIT } from '@xphoto/shared';
import { useI18n } from '../i18n';
import { interpolate } from '../gallery/format';
import { formatBytes, formatDateTime } from '../viewer/format';
import { thumbnailUrl, ApiError } from '../gallery/api';
import { fetchTrash, purgeTrash, restoreAssets } from '../trash/trash-api';
import { Portal } from '../components/Portal';
import { ConfirmModal } from '../taxonomy/ConfirmModal';

/**
 * Section « Poubelle » (Lot 2) — écran de GESTION de la corbeille (soft-delete du Lot 1).
 * Modèle structurel : `FileErrorsSection` (self-contained, pagination, états).
 *
 * L'utilisateur (propriétaire / superadmin) VOIT les médias en corbeille (vignettes),
 * en RESTAURE (Lot 1) et les PURGE DÉFINITIVEMENT (Lot 2 : `unlink` disque + suppression
 * DB, irréversible → derrière une modale de confirmation lourde `ConfirmModal`). Deux
 * portées de purge : la SÉLECTION ou TOUTE la corbeille. La purge s'exécute en JOB
 * asynchrone (à suivre dans la cloche). Un 409 `WRITE_DISABLED` (bibliothèque en lecture
 * seule) affiche un message dédié.
 */

type LoadStatus = 'idle' | 'loading' | 'error' | 'ready';

const PAGE_SIZE = TRASH_DEFAULT_LIMIT;

/** Portée de la purge en attente de confirmation. */
type PendingPurge = { kind: 'selection'; ids: string[] } | { kind: 'all' };

export function TrashSection({
  libraryId,
  canWrite,
}: {
  libraryId: string | null;
  canWrite: boolean;
}): React.JSX.Element {
  const { t, locale } = useI18n();

  const [status, setStatus] = useState<LoadStatus>('idle');
  const [assets, setAssets] = useState<TrashAsset[]>([]);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [reloadKey, setReloadKey] = useState(0);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [pending, setPending] = useState<PendingPurge | null>(null);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  useEffect(() => {
    if (libraryId === null) {
      setStatus('idle');
      return;
    }
    const controller = new AbortController();
    setStatus('loading');
    fetchTrash(libraryId, PAGE_SIZE, offset)
      .then((page) => {
        if (controller.signal.aborted) return;
        setAssets(page.assets);
        setTotal(page.total);
        // Élague la sélection des ids qui ne sont plus présents sur la page courante.
        setSelected((prev) => {
          const present = new Set(page.assets.map((a) => a.id));
          const next = new Set<string>();
          for (const id of prev) if (present.has(id)) next.add(id);
          return next;
        });
        setStatus('ready');
      })
      .catch((err: unknown) => {
        if (controller.signal.aborted) return;
        setStatus('error');
        void err;
      });
    return () => controller.abort();
  }, [libraryId, offset, reloadKey]);

  const reload = useCallback(() => setReloadKey((k) => k + 1), []);

  const toggle = useCallback((id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const restore = useCallback(async () => {
    if (libraryId === null || selected.size === 0 || busy) return;
    setBusy(true);
    setErrorMsg(null);
    setNotice(null);
    try {
      const res = await restoreAssets(libraryId, [...selected]);
      setSelected(new Set());
      setNotice(interpolate(t('admin.trash.restored'), { count: res.restored }, locale));
      setOffset(0);
      reload();
    } catch {
      setErrorMsg(t('admin.trash.error'));
    } finally {
      setBusy(false);
    }
  }, [libraryId, selected, busy, t, reload]);

  const confirmPurge = useCallback(async () => {
    if (libraryId === null || pending === null || busy) return;
    setBusy(true);
    setErrorMsg(null);
    setNotice(null);
    const body: TrashPurgeRequestInput =
      pending.kind === 'all' ? { all: true } : { assetIds: pending.ids };
    try {
      await purgeTrash(libraryId, body);
      setPending(null);
      setSelected(new Set());
      setNotice(t('admin.trash.purgeQueued'));
      setOffset(0);
      reload();
    } catch (err: unknown) {
      if (err instanceof ApiError && err.status === 409 && err.code === 'WRITE_DISABLED') {
        setErrorMsg(t('admin.trash.errorWriteDisabled'));
      } else {
        setErrorMsg(t('admin.trash.error'));
      }
    } finally {
      setBusy(false);
    }
  }, [libraryId, pending, busy, t, reload]);

  const rangeFrom = total === 0 ? 0 : offset + 1;
  const rangeTo = Math.min(offset + assets.length, total);
  const canPrev = offset > 0;
  const canNext = offset + PAGE_SIZE < total;

  return (
    <section
      id="admin-trash"
      role="tabpanel"
      aria-labelledby="admin-trash-heading"
      className="flex flex-col gap-6"
    >
      <div className="flex flex-col gap-1">
        <h3
          id="admin-trash-heading"
          className="flex items-center gap-2 text-base font-semibold text-fg"
        >
          <TrashIcon className="h-5 w-5" aria-hidden="true" />
          {t('admin.trash.title')}
        </h3>
        <p className="text-sm text-muted">{t('admin.trash.intro')}</p>
      </div>

      {libraryId === null ? (
        <p className="py-6 text-center text-muted">{t('admin.noLibrarySelected')}</p>
      ) : status === 'error' ? (
        <TrashLoadError onRetry={reload} />
      ) : status === 'ready' ? (
        <>
          {/* Barre d'actions. Le bloc ENTIER est gardé par `canWrite` (audit A4) :
              « Restaurer » vivait HORS du bloc gardé alors que la restauration est une
              écriture au même titre que la purge (`routes/trash.ts` = `requireLibraryWrite`
              pour les deux). Sans écriture, la barre n'a plus aucun bouton : on ne la
              rend pas du tout plutôt que d'afficher un conteneur vide. */}
          {canWrite && (
            <div className="flex flex-wrap items-center gap-3">
              <button
                type="button"
                disabled={selected.size === 0 || busy}
                onClick={() => void restore()}
                className="inline-flex items-center gap-2 rounded-md border border-border px-3 py-2 text-sm font-medium text-fg hover:bg-surface-2 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand disabled:opacity-50"
              >
                <ArrowUturnLeftIcon className="h-4 w-4" aria-hidden="true" />
                {interpolate(t('admin.trash.restoreSelected'), { count: selected.size }, locale)}
              </button>
              <button
                type="button"
                disabled={selected.size === 0 || busy}
                onClick={() => setPending({ kind: 'selection', ids: [...selected] })}
                className="inline-flex items-center gap-2 rounded-md bg-danger px-3 py-2 text-sm font-medium text-danger-fg focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-danger disabled:opacity-50"
              >
                <TrashIcon className="h-4 w-4" aria-hidden="true" />
                {interpolate(t('admin.trash.purgeSelected'), { count: selected.size }, locale)}
              </button>
              <button
                type="button"
                disabled={total === 0 || busy}
                onClick={() => setPending({ kind: 'all' })}
                className="inline-flex items-center gap-2 rounded-md border border-danger/50 px-3 py-2 text-sm font-medium text-danger hover:bg-surface-2 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-danger disabled:opacity-50"
              >
                <TrashIcon className="h-4 w-4" aria-hidden="true" />
                {t('admin.trash.purgeAll')}
              </button>
            </div>
          )}

          {notice !== null && (
            <p role="status" className="text-sm text-fg">
              {notice}
            </p>
          )}
          {errorMsg !== null && (
            <p role="alert" className="inline-flex items-center gap-1 text-sm text-danger">
              <ExclamationTriangleIcon className="h-5 w-5 shrink-0" aria-hidden="true" />
              {errorMsg}
            </p>
          )}

          <TrashGrid assets={assets} selected={selected} onToggle={toggle} locale={locale} />

          {total > 0 && (
            <div className="flex items-center justify-between gap-3">
              <p className="text-sm text-muted" role="status">
                {interpolate(
                  t('admin.trash.range'),
                  { from: rangeFrom, to: rangeTo, total },
                  locale,
                )}
              </p>
              <div className="flex gap-2">
                <button
                  type="button"
                  disabled={!canPrev}
                  onClick={() => setOffset((o) => Math.max(0, o - PAGE_SIZE))}
                  className="rounded-md border border-border px-3 py-2 text-sm font-medium text-fg hover:bg-surface-2 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand disabled:opacity-50"
                >
                  {t('admin.trash.previous')}
                </button>
                <button
                  type="button"
                  disabled={!canNext}
                  onClick={() => setOffset((o) => o + PAGE_SIZE)}
                  className="rounded-md border border-border px-3 py-2 text-sm font-medium text-fg hover:bg-surface-2 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand disabled:opacity-50"
                >
                  {t('admin.trash.next')}
                </button>
              </div>
            </div>
          )}
        </>
      ) : (
        <p role="status" className="py-8 text-center text-muted">
          {t('admin.loading')}
        </p>
      )}

      {pending !== null && (
        <Portal>
          <ConfirmModal
            title={t('admin.trash.confirmTitle')}
            confirmLabel={t('admin.trash.confirmButton')}
            cancelLabel={t('admin.trash.cancel')}
            busyLabel={t('admin.trash.purging')}
            busy={busy}
            danger
            onConfirm={() => void confirmPurge()}
            onClose={() => {
              if (!busy) setPending(null);
            }}
          >
            <p className="flex items-start gap-2 rounded-md border border-danger/40 bg-surface-2 p-3">
              <ExclamationTriangleIcon
                className="mt-0.5 h-5 w-5 shrink-0 text-danger"
                aria-hidden="true"
              />
              <span>{t('admin.trash.confirmWarning')}</span>
            </p>
            <p className="text-muted">
              {pending.kind === 'all'
                ? interpolate(t('admin.trash.confirmAll'), { count: total }, locale)
                : interpolate(
                    t('admin.trash.confirmSelection'),
                    { count: pending.ids.length },
                    locale,
                  )}
            </p>
          </ConfirmModal>
        </Portal>
      )}
    </section>
  );
}

/** Grille de cartes de médias en corbeille (vignette + nom + date de suppression). */
function TrashGrid({
  assets,
  selected,
  onToggle,
  locale,
}: {
  assets: readonly TrashAsset[];
  selected: ReadonlySet<string>;
  onToggle: (id: string) => void;
  locale: Locale;
}): React.JSX.Element {
  const { t } = useI18n();
  if (assets.length === 0) {
    return <p className="py-8 text-center text-muted">{t('admin.trash.empty')}</p>;
  }
  return (
    <ul className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4">
      {assets.map((asset) => (
        <TrashCard
          key={asset.id}
          asset={asset}
          checked={selected.has(asset.id)}
          onToggle={onToggle}
          locale={locale}
        />
      ))}
    </ul>
  );
}

/** Carte d'un média en corbeille : case à cocher + vignette + nom + taille + date. */
function TrashCard({
  asset,
  checked,
  onToggle,
  locale,
}: {
  asset: TrashAsset;
  checked: boolean;
  onToggle: (id: string) => void;
  locale: Locale;
}): React.JSX.Element {
  const { t } = useI18n();
  const [imgFailed, setImgFailed] = useState(false);
  const deletedWhen = formatDateTime(asset.deletedAt, locale) ?? asset.deletedAt;
  const labelId = `trash-card-${asset.id}`;

  return (
    <li className="flex flex-col overflow-hidden rounded-lg border border-border bg-surface">
      <label
        className="relative flex aspect-square cursor-pointer items-center justify-center bg-surface-2"
        htmlFor={`trash-check-${asset.id}`}
      >
        <input
          id={`trash-check-${asset.id}`}
          type="checkbox"
          checked={checked}
          onChange={() => onToggle(asset.id)}
          aria-labelledby={labelId}
          className="absolute left-2 top-2 z-10 h-4 w-4 rounded border-border-strong text-brand focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand"
        />
        {imgFailed ? (
          <PhotoIcon className="h-10 w-10 text-muted" aria-hidden="true" />
        ) : (
          <img
            src={thumbnailUrl(asset.id, 256)}
            alt=""
            loading="lazy"
            fetchPriority="low"
            onError={() => setImgFailed(true)}
            className="h-full w-full object-cover"
          />
        )}
      </label>
      <div className="flex flex-col gap-0.5 p-2 text-xs">
        <span id={labelId} className="truncate font-medium text-fg" title={asset.relPath}>
          {asset.filename}
        </span>
        <span className="text-muted">{formatBytes(asset.sizeBytes, locale)}</span>
        <span className="text-muted">
          {interpolate(t('admin.trash.deletedWhen'), { when: deletedWhen })}
        </span>
      </div>
    </li>
  );
}

/** État d'erreur de chargement (icône + texte + réessai). */
function TrashLoadError({ onRetry }: { onRetry: () => void }): React.JSX.Element {
  const { t } = useI18n();
  return (
    <div className="flex flex-col items-center gap-3 py-8 text-center">
      <ExclamationTriangleIcon className="h-8 w-8 text-danger" aria-hidden="true" />
      <p className="text-fg">{t('admin.errorTitle')}</p>
      <button
        type="button"
        onClick={onRetry}
        className="rounded-md bg-brand px-3 py-2 text-sm font-medium text-brand-fg focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand"
      >
        {t('admin.retry')}
      </button>
    </div>
  );
}
